import type { ElevationReading } from './elevationQuery';
import type { RouteSample, SampledRoute } from './routeSampling';

export interface ProfilePoint {
  readonly distance: number;
  readonly coordinate: readonly [number, number];
  readonly vertex: boolean;
  /** Metres above EGM2008, or null (water, gap, outside coverage). */
  readonly elevation: number | null;
  readonly reason: ElevationReading['status'];
}

export interface ProfileExtreme { readonly elevation: number; readonly distance: number; readonly index: number }

export interface ProfileStats {
  readonly length: number;
  readonly start: number | null;
  readonly end: number | null;
  readonly min: ProfileExtreme | null;
  readonly max: ProfileExtreme | null;
  /** end − start; null when either end has no data. */
  readonly difference: number | null;
  /** (end − start) / length × 100 — a mean, preliminary figure (it does not describe the intermediate terrain). */
  readonly meanSlopePercent: number | null;
  readonly validSamples: number;
  readonly totalSamples: number;
  /** Fraction of the length (0–1) whose samples have data. */
  readonly coverage: number;
  readonly spacing: number;
}

export interface ElevationProfile {
  readonly points: readonly ProfilePoint[];
  /** Index ranges [from, to] of consecutive points with data: drawn as separate lines, gaps are never bridged. */
  readonly runs: readonly (readonly [number, number])[];
  readonly stats: ProfileStats;
}

export function buildProfile(route: SampledRoute, query: (sample: RouteSample) => ElevationReading): ElevationProfile {
  const points: ProfilePoint[] = route.samples.map((sample) => {
    const reading = query(sample);
    return { distance: sample.distance, coordinate: sample.coordinate, vertex: sample.vertex, elevation: reading.status === 'ok' ? reading.elevation : null, reason: reading.status };
  });
  const runs: [number, number][] = [];
  let min: ProfileExtreme | null = null; let max: ProfileExtreme | null = null; let valid = 0; let coveredLength = 0;
  points.forEach((point, index) => {
    if (point.elevation === null) return;
    valid += 1;
    if (!min || point.elevation < min.elevation) min = { elevation: point.elevation, distance: point.distance, index };
    if (!max || point.elevation > max.elevation) max = { elevation: point.elevation, distance: point.distance, index };
    const last = runs[runs.length - 1];
    if (last && last[1] === index - 1) { last[1] = index; coveredLength += point.distance - points[index - 1]!.distance; } else runs.push([index, index]);
  });
  const start = points[0]?.elevation ?? null; const end = points[points.length - 1]?.elevation ?? null;
  const difference = start !== null && end !== null ? end - start : null;
  return {
    points, runs,
    stats: {
      length: route.length, start, end, min, max, difference,
      meanSlopePercent: difference !== null && route.length > 0 ? difference / route.length * 100 : null,
      validSamples: valid, totalSamples: points.length,
      coverage: route.length > 0 ? coveredLength / route.length : valid ? 1 : 0,
      spacing: route.spacing,
    },
  };
}

/** Index of the point nearest to a distance along the route (binary search; points are sorted by distance). */
export function nearestPointIndex(profile: ElevationProfile, distance: number): number {
  const { points } = profile; if (!points.length) return -1;
  let low = 0; let high = points.length - 1;
  while (high - low > 1) { const mid = (low + high) >> 1; if (points[mid]!.distance < distance) low = mid; else high = mid; }
  return Math.abs(points[low]!.distance - distance) <= Math.abs(points[high]!.distance - distance) ? low : high;
}
